import { useAnalytics } from '@satellite/composables/useAnalytics';
import type { TrackOptions } from '@satellite/analytics/AnalyticsClient';
import type { MoveTaskViewData, TaskViewData } from '@nova/core';
import {
  Events,
  type AssetCheckMissingPayload,
  type CirceEventsMap,
  type ReportedMissingAssetEntryPoint,
  type ReportedMissingAssetPayload,
  type TaskBoardFilteredPayload,
  type TaskBoardSwitchedLanePayload,
  type YardTaskAddedCommentPayload,
  type YardTaskCanceledPayload,
  type YardTaskCompletedPayload,
  type YardTaskCreatedNewMovePayload,
  type YardTaskFlaggedIssuePayload,
  type YardTaskOpenedPayload,
  type YardTaskStartedPayload,
  type YardTaskUnflaggedIssuePayload,
  type YardTaskUpdatedDestinationSpotPayload,
  type YardTaskUpdatedOriginSpotPayload
} from './events';
import * as EventContext from './yardTaskAnalyticsEventContext';

export type TrackYardTaskNewMoveOptions = TrackOptions<YardTaskCreatedNewMovePayload>;
export type TrackYardTaskOpenedOptions = TrackOptions<YardTaskOpenedPayload>;
export type TrackYardTaskStartedOptions = TrackOptions<YardTaskStartedPayload>;
export type TrackYardTaskUpdatedOriginSpotOptions = TrackOptions<YardTaskUpdatedOriginSpotPayload>;
export type TrackYardTaskUpdatedDestinationSpotOptions =
  TrackOptions<YardTaskUpdatedDestinationSpotPayload>;
export type TrackYardTaskCanceledOptions = TrackOptions<YardTaskCanceledPayload>;
export type TrackYardTaskCompletedOptions = TrackOptions<YardTaskCompletedPayload>;
export type TrackYardTaskFlaggedIssueOptions = TrackOptions<YardTaskFlaggedIssuePayload>;
export type TrackYardTaskUnflaggedIssueOptions = TrackOptions<YardTaskUnflaggedIssuePayload>;
export type TrackYardTaskAddedCommentOptions = TrackOptions<YardTaskAddedCommentPayload>;
export type TrackYardTaskFilteredOptions = TrackOptions<TaskBoardFilteredPayload>;
export type TrackYardTaskSwitchedLaneOptions = TrackOptions<TaskBoardSwitchedLanePayload>;
export type TrackYardTaskAssetReportedMissingOptions = TrackOptions<ReportedMissingAssetPayload>;

export function useYardTaskAnalytics() {
  const analytics = useAnalytics<CirceEventsMap>();

  function trackYardTaskCreatedNewMove(
    task: MoveTaskViewData,
    options: TrackYardTaskNewMoveOptions = {}
  ) {
    const payload: YardTaskCreatedNewMovePayload = {
      ...EventContext.buildYardTaskCreatedNewMoveAnalyticsEventContext(task),
      ...(options.additionalPayload ?? {})
    };
    analytics.track(Events.TASK.CREATED_NEW_MOVE, payload);
  }

  function trackYardTaskOpened(task: TaskViewData, options: TrackYardTaskOpenedOptions = {}) {
    const payload: YardTaskOpenedPayload = {
      ...EventContext.buildYardTaskOpenedAnalyticsEventContext(task),
      ...(options.additionalPayload ?? {})
    };
    analytics.track(Events.TASK.OPENED, payload);
  }

  function trackYardTaskStarted(task: TaskViewData, options: TrackYardTaskStartedOptions = {}) {
    const payload: YardTaskStartedPayload = {
      ...EventContext.buildYardTaskStartedAnalyticsEventContext(task),
      ...(options.additionalPayload ?? {})
    };
    analytics.track(Events.TASK.STARTED, payload);
  }

  function trackYardTaskUpdatedOriginSpot(
    task: MoveTaskViewData,
    options: TrackYardTaskUpdatedOriginSpotOptions = {}
  ) {
    const payload: YardTaskUpdatedOriginSpotPayload = {
      ...EventContext.buildYardTaskUpdatedOriginSpotAnalyticsEventContext(task),
      ...(options.additionalPayload ?? {})
    };
    analytics.track(Events.TASK.UPDATED_ORIGIN_SPOT, payload);
  }

  function trackYardTaskUpdatedDestinationSpot(
    task: MoveTaskViewData,
    options: TrackYardTaskUpdatedDestinationSpotOptions = {}
  ) {
    const payload: YardTaskUpdatedDestinationSpotPayload = {
      ...EventContext.buildYardTaskUpdatedDestinationSpotAnalyticsEventContext(task),
      ...(options.additionalPayload ?? {})
    };
    analytics.track(Events.TASK.UPDATED_DESTINATION_SPOT, payload);
  }

  function trackYardTaskCanceled(task: TaskViewData, options: TrackYardTaskCanceledOptions = {}) {
    const payload: YardTaskCanceledPayload = {
      ...EventContext.buildYardTaskCanceledAnalyticsEventContext(task),
      ...(options.additionalPayload ?? {})
    };
    analytics.track(Events.TASK.CANCELED, payload);
  }

  function trackYardTaskCompleted(task: TaskViewData, options: TrackYardTaskCompletedOptions = {}) {
    const payload: YardTaskCompletedPayload = {
      ...EventContext.buildYardTaskCompletedAnalyticsEventContext(task),
      ...(options.additionalPayload ?? {})
    };
    analytics.track(Events.TASK.COMPLETED, payload);
  }

  function trackYardTaskFlaggedIssue(
    task: Partial<TaskViewData>,
    options: TrackYardTaskFlaggedIssueOptions = {}
  ) {
    const payload: YardTaskFlaggedIssuePayload = {
      ...EventContext.buildYardTaskFlaggedIssueAnalyticsEventContext(task),
      ...(options.additionalPayload ?? {})
    };
    analytics.track(Events.TASK.FLAGGED_ISSUE, payload);
  }

  function trackYardTaskUnflaggedIssue(
    task: Partial<TaskViewData>,
    options: TrackYardTaskUnflaggedIssueOptions = {}
  ) {
    const payload: YardTaskUnflaggedIssuePayload = {
      ...EventContext.buildYardTaskUnflaggedIssueAnalyticsEventContext(task),
      ...(options.additionalPayload ?? {})
    };
    analytics.track(Events.TASK.UNFLAGGED_ISSUE, payload);
  }

  function trackYardTaskAddedComment(
    task: TaskViewData,
    options: TrackYardTaskAddedCommentOptions = {}
  ) {
    const payload: YardTaskAddedCommentPayload = {
      ...EventContext.buildYardTaskAddedCommentAnalyticsEventContext(task),
      ...(options.additionalPayload ?? {})
    };
    analytics.track(Events.TASK.ADDED_COMMENT, payload);
  }

  function trackYardTaskFiltered(
    params: TaskBoardFilteredPayload,
    options: TrackYardTaskFilteredOptions = {}
  ) {
    const payload: TaskBoardFilteredPayload = {
      ...EventContext.buildYardTaskFilteredAnalyticsEventContext(params),
      ...(options.additionalPayload ?? {})
    };
    analytics.track(Events.TASK_BOARD.FILTERED, payload);
  }

  function trackYardTaskWarehouseChanged() {
    analytics.track(Events.WAREHOUSE.CHANGED);
  }

  function trackYardTaskSwitchedLane(
    switchedToLane: string,
    options: TrackYardTaskSwitchedLaneOptions = {}
  ) {
    const payload: TaskBoardSwitchedLanePayload = {
      ...EventContext.buildYardTaskSwitchedLaneAnalyticsEventContext(switchedToLane),
      ...(options.additionalPayload ?? {})
    };
    analytics.track(Events.TASK_BOARD.SWITCHED_LANE, payload);
  }

  function trackAssetCheckMissing(payload: AssetCheckMissingPayload) {
    analytics.track(Events.TASK.ASSET_CHECK_MISSING, payload);
  }

  function trackYardTaskAssetReportedMissing(
    payload: Omit<ReportedMissingAssetPayload, 'entry_point'> & {
      entry_point?: ReportedMissingAssetEntryPoint;
    },
    options: TrackYardTaskAssetReportedMissingOptions = {}
  ) {
    const resolvedPayload: ReportedMissingAssetPayload = {
      ...payload,
      entry_point: payload.entry_point ?? 'task_detail',
      ...(options.additionalPayload ?? {})
    };
    analytics.track(Events.TASK.REPORTED_MISSING_ASSET, resolvedPayload);
  }

  return {
    trackAssetCheckMissing,
    trackYardTaskAssetReportedMissing,
    trackYardTaskOpened,
    trackYardTaskCreatedNewMove,
    trackYardTaskStarted,
    trackYardTaskUpdatedOriginSpot,
    trackYardTaskUpdatedDestinationSpot,
    trackYardTaskCanceled,
    trackYardTaskCompleted,
    trackYardTaskFlaggedIssue,
    trackYardTaskUnflaggedIssue,
    trackYardTaskAddedComment,
    trackYardTaskFiltered,
    trackYardTaskWarehouseChanged,
    trackYardTaskSwitchedLane
  };
}
